<template>
  <v-container>
    <h1>Produkt: {{ product.name }}</h1>

    <AdminBreadcrumbs :vendor="vendor" tail-text="Rediger produkt" />

    <confirm ref="confirm"></confirm>

    <v-card class="orange lighten-5" elevation="3">
      <v-card-title class="white--text orange"> Detaljer </v-card-title>

      <v-form>
        <v-container>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="product.name"
                label="Navn"
                required
                :disabled="isSaving"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="product.price"
                label="Pris"
                required
                :disabled="isSaving"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="product.description"
                label="Beskrivelse"
                required
                :disabled="isSaving"
              ></v-textarea>
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="product.vat_class"
                :items="vat_classes"
                item-value="id"
                label="Momsklasse"
                single-line
              >
                <template slot="selection" slot-scope="data">
                  {{ data.item.name }} ({{ data.item.percentage }}%)
                </template>
                <template slot="item" slot-scope="data">
                  {{ data.item.name }} ({{ data.item.percentage }}%)
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-btn color="success" :loading="isSaving" @click="saveProduct">
                <v-icon left> mdi-content-save </v-icon>
                Lagre
              </v-btn>
            </v-col>
            <v-col cols="6" class="text-right">
              <v-btn color="error" @click="deleteProduct">
                <v-icon left> mdi-delete </v-icon>
                Slett
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </v-card>

    <v-card class="orange lighten-5 mt-5" elevation="3">
      <v-card-title class="white--text orange"> Bilde </v-card-title>

      <v-form>
        <v-container>
          <v-row>
            <v-col cols="12">
              <v-file-input
                v-model="productImage"
                accept="image/png, image/jpeg"
                placeholder="Produktbilde"
                prepend-icon="mdi-camera"
                label="Produktbilde"
              ></v-file-input>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <v-btn
                :loading="isSavingImage"
                :disabled="!canUpload || isSavingImage"
                color="success"
                @click="saveProductImage"
              >
                <v-icon left> mdi-content-save </v-icon>
                Last opp bilde
              </v-btn>
            </v-col>
            <v-col cols="6" class="text-right">
              <v-btn
                :disabled="!hasImage"
                color="error"
                @click="deleteProductImage"
              >
                <v-icon left> mdi-delete </v-icon>
                Slett bilde
              </v-btn>
            </v-col>
          </v-row>

          <v-row v-if="hasImage">
            <v-col cols="12">
              <v-img :src="product.image" class="product-image" />
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </v-card>
  </v-container>
</template>

<script>
import backend from "@/backend";
import AdminBreadcrumbs from "@/components/AdminBreadcrumbs";
import Confirm from "@/components/Confirm";

export default {
  name: "AdminProductDetail",

  components: {
    Confirm,
    AdminBreadcrumbs,
  },

  props: {
    vendorId: {
      type: String,
      required: true,
    },
    productId: {
      type: String,
      required: true,
    },
  },

  data: function () {
    return {
      productImage: null,
      vat_classes: [],
      vendor: {},
      product: {},
      isSaving: false,
      isSavingImage: false,
      isLoading: true,
      showDelete: false,
    };
  },

  computed: {
    hasImage() {
      return this.product.image !== null;
    },

    canUpload() {
      return this.productImage !== null;
    },
  },

  async created() {
    this.vendor = await backend.get(`vendors/${this.vendorId}/`);

    this.product = await backend.get(`products/${this.productId}/`, {
      vendor: this.vendorId,
    });

    this.vat_classes = await backend.get("vat/");
  },

  methods: {
    async deleteProduct() {
      if (
        await this.$refs.confirm.open(
          "Bekreft sletting",
          "Er du sikker på at du vil slette dette produktet?",
          { color: "red" }
        )
      ) {
        // Not really saving but cute to disable the form?
        this.isSaving = true;

        await backend.del(
          `products/${this.product.id}/`,
          {},
          { vendor: this.vendor.id }
        );

        this.$router.push({
          name: "adminProducts",
          params: {
            vendorId: this.vendorId,
          },
        });

        this.$store.dispatch("add", {
          type: "success",
          message: "Produktet er slettet.",
        });
      }
    },

    async deleteProductImage() {
      if (
        await this.$refs.confirm.open(
          "Bekreft sletting",
          "Er du sikker på at du vil slette produktbildet?",
          { color: "red" }
        )
      ) {
        this.product = await backend.post(
          `products/${this.product.id}/delete_image/`,
          {},
          {
            vendor: this.vendorId,
          }
        );

        this.$store.dispatch("add", {
          type: "success",
          message: "Produktbildet er slettet.",
        });
      }
    },
    async saveProductImage() {
      const file = this.productImage;
      let formData = new FormData();
      formData.append("productimage", file);

      this.isSavingImage = true;

      this.product = await backend.post(
        `products/${this.product.id}/upload_image/`,
        formData,
        {
          vendor: this.vendorId,
        }
      );

      this.productImage = null;
      this.isSavingImage = false;

      this.$store.dispatch("add", {
        type: "success",
        message: "Produktbildet er nå lagret.",
      });
    },
    async saveProduct() {
      this.isSaving = true;
      this.product = await backend.put(
        `products/${this.product.id}/`,
        {
          name: this.product.name,
          description: this.product.description,
          price: this.product.price,
          vendor: this.product.vendor,
          vat_class: this.product.vat_class,
        },
        {
          vendor: this.vendorId,
        }
      );

      this.isSaving = false;

      this.$router.push({
        name: "adminProducts",
        params: {
          vendorId: this.vendorId,
        },
      });

      this.$store.dispatch("add", {
        type: "success",
        message: "Produktet er nå lagret.",
      });
    },
  },
};
</script>

<style scoped>
.product-image {
  max-width: 20em;
}
</style>
